import React, { useEffect, useState, useMemo } from 'react';
import { Box, Typography, IconButton, CircularProgress, Button } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import DirectionsOutlinedIcon from '@mui/icons-material/DirectionsOutlined';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import { resolveItemRef } from '../../../utils/itemDetailPath';
import { openDirections } from '../../../utils/openDirections';

const API = () => process.env.REACT_APP_API_URL;

const TYPE_META = {
  event: { label: { TH: 'กิจกรรม', EN: 'Event', CN: '活动' }, color: '#7c3aed' },
  review: { label: { TH: 'รีวิว', EN: 'Review', CN: '评论' }, color: '#0ea5e9' },
  venue: { label: { TH: 'สถานที่', EN: 'Venue', CN: '地点' }, color: '#10b981' },
};

const firstVenueImage = (vi) => {
  if (!Array.isArray(vi) || vi.length === 0) return null;
  const f = vi[0];
  if (!f) return null;
  return typeof f === 'string' ? f : (f.url || f.image_url || f.src || null);
};

const formatDate = (d) => {
  if (!d) return '';
  try { return new Date(d).toLocaleDateString(); } catch { return ''; }
};

// Build a display model from the fetched record + the item we already had.
const buildView = (type, data, fallback, language) => {
  const s = (language || 'TH').toLowerCase(); // th | en | cn
  const pick = (obj, base) =>
    obj[`${base}_${s}`] || obj[`${base}_th`] || obj[`${base}_en`] || obj[`${base}_cn`] || obj[base] || '';

  const fbImage = fallback?.item_image_url || fallback?.image_url || fallback?.event_image_link || null;
  const fbName = fallback?.item_name || fallback?.event_name || fallback?.name || '';
  const fbLoc = fallback?.item_location || fallback?.event_location || '';
  const lat = data?.latitude ?? fallback?.latitude ?? null;
  const lng = data?.longitude ?? fallback?.longitude ?? null;

  if (type === 'venue') {
    return {
      title: pick(data, 'name') || fbName || 'Venue',
      image: data.generated_image_url || firstVenueImage(data.venue_images) || data.generated_thumbnail_url || data.logo_url || fbImage,
      location: data.google_address || fbLoc,
      date: '',
      description: pick(data, 'description') || pick(data, 'summary') || pick(data, 'traveller_note') || '',
      rating: data.rating != null ? Number(data.rating) : null,
      creator: '',
      website: data.official_page_url || null,
      cuisine: data.cuisine_type || '',
      latitude: lat, longitude: lng,
    };
  }

  if (type === 'review') {
    return {
      title: pick(data, 'place_name') || data.review_title || fbName || 'Review',
      image: data.main_image_url || data.image_link || data.generated_thumbnail_url || fbImage,
      location: data.location_name || data.place_name || fbLoc,
      date: formatDate(data.created_at),
      description: pick(data, 'review_content') || data.summary || '',
      rating: data.rating != null ? Number(data.rating) : null,
      creator: data.reviewer_name || data.user_name || '',
      website: null,
      cuisine: '',
      latitude: lat, longitude: lng,
    };
  }

  // event
  return {
    title: data.event_name || fbName || 'Event',
    image: data.generated_image_url || data.event_image_link || data.image_url || data.main_image_url || fbImage,
    location: data.event_location || data.location || fbLoc,
    date: formatDate(data.event_date || data.start_date),
    description: data.event_summary || data.event_description || data.summary || data.description || '',
    rating: null,
    creator: data.creator_name || data.organizer || data.user_name || '',
    website: data.website_link || data.website || null,
    cuisine: '',
    latitude: lat, longitude: lng,
  };
};

/**
 * SavedItemDetail — an in-panel, professionally styled detail view for a saved
 * or reviewer item. Renders inside the saved panel's tab area (it does NOT
 * navigate away). Fetches the full record from the API and falls back to the
 * fields already on the clicked item if the request fails.
 */
const SavedItemDetail = ({ item, language = 'TH', onBack, onViewOnMap }) => {
  const ref = useMemo(() => resolveItemRef(item), [item]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!ref) { setLoading(false); return undefined; }
    let cancelled = false;
    setLoading(true);
    setError(false);
    const url = ref.type === 'review' ? `${API()}/reviews/${ref.id}`
      : ref.type === 'venue' ? `${API()}/venues/${ref.id}`
        : `${API()}/events/${ref.id}`;
    fetch(url)
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((d) => { if (!cancelled) setData(d || {}); })
      .catch(() => { if (!cancelled) setError(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [ref]);

  const type = ref?.type || 'event';
  const meta = TYPE_META[type] || TYPE_META.event;
  const view = useMemo(() => buildView(type, data || {}, item, language), [type, data, item, language]);

  const backLabel = language === 'TH' ? 'กลับ' : language === 'CN' ? '返回' : 'Back';

  return (
    <Box sx={{ height: '100%', overflowY: 'auto', bgcolor: '#f7f8fa' }}>
      {/* Sticky back bar */}
      <Box sx={{
        position: 'sticky', top: 0, zIndex: 3,
        display: 'flex', alignItems: 'center', gap: 1,
        px: 1.5, py: 1,
        bgcolor: 'rgba(255,255,255,0.9)',
        backdropFilter: 'blur(8px)',
        borderBottom: '1px solid #eef0f4',
      }}>
        <IconButton size="small" onClick={onBack} aria-label={backLabel}>
          <ArrowBackIcon />
        </IconButton>
        <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: 'text.secondary' }}>{backLabel}</Typography>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 220 }}>
          <CircularProgress size={26} sx={{ color: '#7c3aed' }} />
        </Box>
      ) : (
        <Box sx={{ p: 1.5 }}>
          {/* Hero */}
          <Box sx={{
            position: 'relative',
            borderRadius: 3,
            overflow: 'hidden',
            aspectRatio: '16 / 10',
            backgroundColor: '#e9edf2',
            boxShadow: '0 8px 24px rgba(17,24,39,0.10)',
          }}>
            {view.image ? (
              <Box component="img" src={view.image} alt={view.title}
                sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center' }}>
                <PlaceOutlinedIcon sx={{ fontSize: 42, color: '#b6bdc7' }} />
              </Box>
            )}
            <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(0,0,0,0) 40%, rgba(0,0,0,0.72) 100%)' }} />
            {/* Type badge */}
            <Box sx={{
              position: 'absolute', top: 12, left: 12,
              px: 1.1, py: 0.35, borderRadius: '999px',
              backgroundColor: meta.color,
              color: '#fff', fontSize: '0.68rem', fontWeight: 800, letterSpacing: '0.3px',
              boxShadow: '0 4px 10px rgba(0,0,0,0.25)',
            }}>
              {(meta.label[language] || meta.label.EN).toUpperCase()}
            </Box>
            {/* Title over image */}
            <Typography sx={{
              position: 'absolute', left: 14, right: 14, bottom: 12,
              color: '#fff', fontWeight: 800, fontSize: '1.15rem', lineHeight: 1.25,
              textShadow: '0 2px 10px rgba(0,0,0,0.5)',
              display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden',
            }}>
              {view.title}
            </Typography>
          </Box>

          {/* Meta chips */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1.5 }}>
            {view.rating ? (
              <Box sx={metaChip}>
                <StarRoundedIcon sx={{ fontSize: 16, color: '#f59e0b' }} />
                <span>{view.rating.toFixed(1)}</span>
              </Box>
            ) : null}
            {view.date ? (
              <Box sx={metaChip}>
                <CalendarTodayOutlinedIcon sx={{ fontSize: 14, color: meta.color }} />
                <span>{view.date}</span>
              </Box>
            ) : null}
            {view.cuisine ? (
              <Box sx={metaChip}><span>{view.cuisine}</span></Box>
            ) : null}
          </Box>

          {/* Creator */}
          {view.creator ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 1.5, color: 'text.secondary' }}>
              <Box sx={{ width: 30, height: 30, borderRadius: '50%', bgcolor: 'rgba(124,58,237,0.1)', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
                <PersonOutlineIcon sx={{ fontSize: 18, color: '#7c3aed' }} />
              </Box>
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: 'text.primary' }}>{view.creator}</Typography>
            </Box>
          ) : null}

          {/* Location */}
          {view.location ? (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, mt: 1.5 }}>
              <PlaceOutlinedIcon sx={{ fontSize: 18, color: meta.color, mt: '1px', flexShrink: 0 }} />
              <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary', lineHeight: 1.5 }}>{view.location}</Typography>
            </Box>
          ) : null}

          {/* Description */}
          {view.description ? (
            <Typography sx={{ mt: 1.75, fontSize: '0.9rem', color: '#374151', lineHeight: 1.7, whiteSpace: 'pre-line' }}>
              {view.description}
            </Typography>
          ) : (
            error && (
              <Typography sx={{ mt: 1.75, fontSize: '0.85rem', color: 'text.secondary', fontStyle: 'italic' }}>
                {language === 'TH' ? 'ไม่สามารถโหลดรายละเอียดเพิ่มเติมได้' : language === 'CN' ? '无法加载更多详情' : 'Could not load more details.'}
              </Typography>
            )
          )}

          {/* Actions */}
          <Box sx={{ display: 'flex', gap: 1, mt: 2.25, flexWrap: 'wrap' }}>
            {(onViewOnMap && view.latitude != null && view.longitude != null) && (
              <Button
                variant="contained"
                startIcon={<MapOutlinedIcon />}
                onClick={() => onViewOnMap(Number(view.latitude), Number(view.longitude))}
                sx={{ textTransform: 'none', borderRadius: '999px', fontWeight: 700, boxShadow: 'none', bgcolor: '#7c3aed', '&:hover': { bgcolor: '#6d28d9' } }}
              >
                {language === 'TH' ? 'ดูบนแผนที่' : language === 'CN' ? '在地图上查看' : 'View on map'}
              </Button>
            )}
            {(view.latitude != null && view.longitude != null) && (
              <Button
                variant="outlined"
                startIcon={<DirectionsOutlinedIcon />}
                onClick={() => openDirections({ latitude: view.latitude, longitude: view.longitude, address: view.location, language })}
                sx={{ textTransform: 'none', borderRadius: '999px', fontWeight: 700, color: '#7c3aed', borderColor: '#d6ccf7', '&:hover': { borderColor: '#7c3aed', bgcolor: 'rgba(124,58,237,0.04)' } }}
              >
                {language === 'TH' ? 'เส้นทาง' : language === 'CN' ? '路线' : 'Directions'}
              </Button>
            )}
            {view.website && (
              <Button
                variant="outlined"
                startIcon={<LanguageOutlinedIcon />}
                onClick={() => window.open(view.website, '_blank', 'noopener,noreferrer')}
                sx={{ textTransform: 'none', borderRadius: '999px', fontWeight: 700, color: '#374151', borderColor: '#e5e7eb', '&:hover': { borderColor: '#9ca3af', bgcolor: 'rgba(0,0,0,0.02)' } }}
              >
                {language === 'TH' ? 'เว็บไซต์' : language === 'CN' ? '网站' : 'Website'}
              </Button>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

const metaChip = {
  display: 'flex', alignItems: 'center', gap: 0.5,
  px: 1, py: 0.4, borderRadius: '999px',
  backgroundColor: '#fff', border: '1px solid #eef0f4',
  fontSize: '0.78rem', fontWeight: 700, color: '#374151',
  boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
};

export default SavedItemDetail;
